import * as React from 'react';

import redirect, { setLocation } from 'js/lib/coursera.redirect';

import { Typography2 } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/coursera-ui';
import { Button, StyleSheet, css } from '@coursera/coursera-ui';

import { CourseAndPartnerNameWithModal } from 'bundles/course-cards/components/course-card/enterprise/CourseAndPartnerName';
import withLogin from 'bundles/coursera-ui/components/hocs/withLogin';
import StartDateButton from 'bundles/enterprise-legacy-learner-home/components/StartDateButton';
import ProgramActionButtonContainer from 'bundles/enterprise-legacy-learner-home/components/programActionButton/ProgramActionButtonContainer';
import { PRODUCT_TYPES } from 'bundles/enterprise-legacy-learner-home/constants/ProgramActionConstants';
import withMiniModal, { DESCRIPTION_PAGE_PATHS } from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import type CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import { useOrgSlug } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import { getCourseProductUrl } from 'bundles/unified-description-page-common/utils';
import { useEnterpriseBadgeMetadata } from 'bundles/unified-home-common/contexts/EnterpriseBadgeMetadataContext';

import _t from 'i18n!nls/course-cards';

const ViewDetailsBtnWithMiniModal = withMiniModal<ButtonProps>()(Button);
const ProgramActionButtonWithLogin = withLogin(ProgramActionButtonContainer);

const styles = StyleSheet.create({
  CourseNotEnrolledCard: {
    minHeight: 104,
  },
  notStartedAndButtonsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
  },
});

type Props = {
  course: CoursesV1;
  isPartOfS12n?: boolean;
  programId: string;
  collectionId?: string;
  hideAdditionalInfo?: boolean;
};

const CourseNotEnrolledCard = ({
  course: { id, name, slug, isPreEnroll, isLaunched, isDraft, partnerIds },
  isPartOfS12n,
  programId,
  collectionId,
}: Props): JSX.Element => {
  // We are allowing enrollment for draft courses now
  const shouldRenderEnrollBtn = isLaunched || isDraft || isPreEnroll;
  const btnLabelText = isPreEnroll ? _t('Pre-enroll') : _t('Enroll');
  const badgeMetadata = useEnterpriseBadgeMetadata();
  const programSlug = badgeMetadata?.programSlug;
  const orgSlug = useOrgSlug();

  // Org-scoped home loads the /organizations/ learn link; otherwise fall back to the /programs/ link.
  const navigateToCourseLearnPage = () => {
    if (orgSlug) {
      setLocation(`/organizations/${orgSlug}/learn/${slug}`);
    } else if (programSlug) {
      setLocation(`/programs/${programSlug}/learn/${slug}`);
    }
  };

  const miniModalProps = {
    id,
    isCourse: true,
    tag: undefined,
    unifiedDescriptionPageProps: { slug, productPagePath: DESCRIPTION_PAGE_PATHS.course },
  };
  const btnLabel = <StartDateButton courseId={id} />;

  const onActionSuccess = () => {
    const baseHomeUrl = getCourseProductUrl(slug, false);
    redirect.setLocation(`${baseHomeUrl}/home`);
  };

  const buttons = (
    <React.Fragment>
      <ViewDetailsBtnWithMiniModal
        miniModalProps={miniModalProps}
        type="link"
        size="sm"
        label={_t('View Details')}
        htmlAttributes={{
          'aria-label': `${_t('View Details')}: ${name}`,
        }}
        onClick={navigateToCourseLearnPage}
      />
      {shouldRenderEnrollBtn && (
        <ProgramActionButtonWithLogin
          loggedoutBtnAttributes={{
            label: btnLabelText,
            size: 'sm',
          }}
          loginHashHistoryReplacement={`autoEnroll/course/${id}`}
          programId={programId}
          productId={id}
          collectionId={collectionId}
          onActionSuccess={onActionSuccess}
          renderNothingIfConditionNotMet
          productType={PRODUCT_TYPES.COURSE}
          actionType="ENROLL"
          initialBtnLabel={btnLabel}
          btnAttributes={{
            type: 'primary',
            size: 'sm',
            htmlAttributes: {
              'data-e2e': `ProgramActionButtonContainer~${id}`,
            },
          }}
          trackingInfo={{
            trackingData: { courseId: id },
            trackingName: 'course_enroll_button',
          }}
        />
      )}
    </React.Fragment>
  );

  return (
    <div
      {...css('rc-CourseNotEnrolledCard horizontal-box wrap', styles.CourseNotEnrolledCard)}
      data-e2e={`CourseNotEnrolledCard~${id}`}
    >
      <div className="flex-1 vertical-box">
        <CourseAndPartnerNameWithModal
          courseId={id}
          courseName={name}
          partnerIds={partnerIds}
          isPartOfS12n={isPartOfS12n}
          onClick={navigateToCourseLearnPage}
          miniModalProps={miniModalProps}
        />
        <div {...css(styles.notStartedAndButtonsRow)}>
          {isPartOfS12n && (
            <Typography2 component="p" variant="bodyPrimary">
              {_t('Not Started')}
            </Typography2>
          )}
          {buttons}
        </div>
      </div>
    </div>
  );
};

export default CourseNotEnrolledCard;
